import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  Fuel, Lightbulb, Wifi, AppWindow, MoreHorizontal, Plus, Search, 
  Download, Edit2, Trash2, FileText, ArrowUpDown, DollarSign, Landmark, Truck
} from 'lucide-react';
import { exportToPDF } from '@/utils/exportPDF';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useOperationalExpenses } from '@/hooks/useOperationalExpenses';
import { useBankAccounts } from '@/hooks/useBankAccounts';
import { useBatchSelection } from '@/hooks/useBatchSelection';
import BatchActionBar from '@/components/ui/BatchActionBar';
import { Checkbox } from '@/components/ui/checkbox';
import AsaMutasiConfirm from '@/components/finance/AsaMutasiConfirm';
import CustomDatePicker from '@/components/shared/CustomDatePicker';

const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const EXPENSE_CATEGORIES = [
  { key: 'Gaji & Bonus', label: 'Gaji & Bonus', icon: DollarSign, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { key: 'Bensin', label: 'Bensin & Transportasi', icon: Fuel, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { key: 'Listrik', label: 'Listrik & Air', icon: Lightbulb, color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  { key: 'Internet', label: 'Internet & Pulsa', icon: Wifi, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { key: 'Langganan Aplikasi', label: 'Langganan Aplikasi', icon: AppWindow, color: 'bg-purple-50 text-purple-700 border-purple-200' },
  { key: 'Iklan & Promosi', label: 'Iklan & Promosi', icon: FileText, color: 'bg-rose-50 text-rose-700 border-rose-200' },
  { key: 'Ongkir Pabrik', label: 'Ongkir (Tanggungan Pabrik)', icon: Truck, color: 'bg-teal-50 text-teal-700 border-teal-200' },
  { key: 'Lainnya', label: 'Lainnya', icon: MoreHorizontal, color: 'bg-slate-50 text-slate-700 border-slate-200' }
];

const REQUESTERS = ['Office', 'Produksi', 'Marketing', 'Sales', 'Semuanya', 'Lainnya'];

export default function FinanceOperational() {
  const queryClient = useQueryClient();
  
  const { accountsMap: accounts = {} } = useBankAccounts();

  const { data: coaList = [] } = useQuery({ 
    queryKey: ['coa'], 
    queryFn: () => base44.entities.ChartOfAccounts.list() 
  });

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.AccountMutation.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-mutations'] });
    }
  });

  const { expenses = [], isLoading: loadingExpenses, createExpense: addExpense, updateExpense, deleteExpense } = useOperationalExpenses();

  const { data: accountMutations = [] } = useQuery({
    queryKey: ['account-mutations'],
    queryFn: () => base44.entities.AccountMutation.list('-created_date'),
    staleTime: 5 * 60 * 1000,
  });

  const mergedExpenses = useMemo(() => {
    return expenses;
  }, [expenses]);

  const {
    selectedIds,
    isSelectionMode,
    toggleSelection,
    selectAll,
    clearSelection,
    startSelection,
    selectedCount,
    totalCount
  } = useBatchSelection(expenses, e => e.id);

  const batchDeleteMutation = useMutation({
    mutationFn: async (ids) => {
      for (const id of ids) {
        await deleteExpense(id);
      }
    },
    onSuccess: () => {
      toast.success(`${selectedCount} biaya operasional berhasil dihapus.`);
      clearSelection();
    }
  });

  const [search, setSearch] = useState(() => {
    if (typeof window !== 'undefined') {
      const p = new URLSearchParams(window.location.search);
      return p.get('search') || '';
    }
    return '';
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const p = new URLSearchParams(window.location.search);
      const q = p.get('search');
      if (q) setSearch(q);
    }
  }, []);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // CRUD Form States
  const [showFormModal, setShowFormModal] = useState(false);
  const [asaConfirmData, setAsaConfirmData] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    category: 'Lainnya',
    requester: 'Office',
    bankAccountId: 'mandiri',
    amount: '',
    description: ''
  });



  // Filter Logic
  const filteredExpenses = useMemo(() => {
    return mergedExpenses.filter(exp => {
      const desc = exp.description || '';
      const cat = exp.category || '';
      const date = exp.date || '';
      const matchSearch = !search || 
        desc.toLowerCase().includes(search.toLowerCase()) ||
        cat.toLowerCase().includes(search.toLowerCase());
      const matchCat = categoryFilter === 'all' || cat === categoryFilter;
      const matchFrom = !dateFrom || date >= dateFrom;
      const matchTo = !dateTo || date <= dateTo;
      return matchSearch && matchCat && matchFrom && matchTo;
    }).sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  }, [expenses, search, categoryFilter, dateFrom, dateTo]);

  const totalPages = Math.ceil(filteredExpenses.length / itemsPerPage) || 1;
  const paginatedExpenses = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredExpenses.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredExpenses, currentPage, itemsPerPage]);

  const totalAmount = useMemo(() => {
    return filteredExpenses.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  }, [filteredExpenses]);

  const categoryBreakdown = useMemo(() => {
    const breakdown = {};
    EXPENSE_CATEGORIES.forEach(c => { breakdown[c.key] = 0; });
    filteredExpenses.forEach(exp => {
      const cat = exp.category || 'Lainnya';
      breakdown[cat] = (breakdown[cat] || 0) + (Number(exp.amount) || 0);
    });
    return breakdown;
  }, [filteredExpenses]);

  const largestCategory = useMemo(() => {
    let maxCat = '—';
    let maxVal = 0;
    Object.entries(categoryBreakdown).forEach(([cat, val]) => {
      if (val > maxVal) {
        maxVal = val;
        maxCat = cat;
      }
    });
    return { name: maxCat, value: maxVal };
  }, [categoryBreakdown]);

  // Actions
  const handleOpenAdd = () => {
    setEditItem(null);
    setForm({
      date: new Date().toISOString().split('T')[0],
      category: 'Lainnya',
      requester: 'Office',
      bankAccountId: 'bca',
      amount: '',
      description: '',
      coa_allocations: []
    });
    setShowFormModal(true);
  };

  const handleOpenEdit = (item) => {
    setEditItem(item);
    setForm({
      date: item.date,
      category: item.category,
      requester: item.requester || 'Office',
      bankAccountId: item.bankAccountId || 'bca',
      amount: item.amount.toString(),
      description: item.description,
      coa_allocations: item.coa_allocations || []
    });
    setShowFormModal(true);
  };

  const handleDelete = async (id) => {
    if (confirm('Apakah Anda yakin ingin menghapus biaya operasional ini?')) {
      await deleteExpense(id);
      toast.success('Biaya operasional berhasil dihapus.');
    }
  };

  const processSaveEdit = async (payload) => {
    try {
      await updateExpense(editItem.id, payload);
      toast.success('Biaya operasional berhasil diperbarui.');
      setShowFormModal(false);
      setAsaConfirmData(null);
    } catch (err) {
      console.error(err);
      toast.error('Gagal memperbarui pengeluaran.');
      if (asaConfirmData) setAsaConfirmData(prev => ({ ...prev, isProcessing: false }));
    }
  };

  const handleAsaConfirm = async (shouldUpdate) => {
    setAsaConfirmData(prev => ({ ...prev, isProcessing: true }));
    if (shouldUpdate && asaConfirmData) {
      try {
        const existingMutations = await base44.entities.AccountMutation.filter({ reference_id: asaConfirmData.refId });
        if (existingMutations && existingMutations.length > 0) {
           const mut = existingMutations[0];
           await base44.entities.AccountMutation.update(mut.id, { 
              amount: mut.amount + asaConfirmData.diff 
           });
        }
      } catch (err) {
        console.error("Gagal update mutasi:", err);
      }
    }
    await processSaveEdit(asaConfirmData.payload);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0 || !form.description) {
      toast.error('Mohon lengkapi semua field dengan benar.');
      return;
    }

    const payload = {
      date: form.date,
      category: form.category,
      requester: form.requester,
      bankAccountId: form.bankAccountId || 'bca',
      amount: Number(form.amount),
      description: form.description,
      coa_allocations: form.coa_allocations
    };

    try {
      if (editItem) {
        const diff = payload.amount - (editItem.amount || 0);
        const refId = editItem.batch_id || `opr-${editItem.id}`;
        
        if (diff !== 0) {
           setAsaConfirmData({
             oldAmount: editItem.amount || 0,
             newAmount: payload.amount,
             date: payload.date,
             diff,
             refId,
             payload,
             isProcessing: false
           });
           return; // Pause execution, wait for ASA modal
        }
        
        await processSaveEdit(payload);
      } else {
        const newExp = await addExpense(payload);
        toast.success('Biaya operasional baru berhasil ditambahkan.');
        setShowFormModal(false);
      }
    } catch (err) {
      console.error(err);
      toast.error('Gagal menyimpan pengeluaran.');
    }
  };

  const handleExportPDF = () => {
    exportToPDF({
      title: 'Laporan Biaya Operasional Satala Dermatech',
      subtitle: `Periode: ${dateFrom || 'Semua'} s/d ${dateTo || 'Semua'} | Total Pengeluaran: Rp ${fmt(totalAmount)}`,
      filename: `Biaya-Operasional-${new Date().toISOString().split('T')[0]}`,
      columns: [
        { key: 'date', label: 'Tanggal', render: r => r.date },
        { key: 'category', label: 'Kategori', render: r => r.category },
        { key: 'description', label: 'Keterangan', render: r => r.description },
        { key: 'amount', label: 'Jumlah (Rp)', render: r => `Rp ${fmt(r.amount)}` }
      ],
      rows: filteredExpenses
    });
  };

  return (
    <div className="space-y-8 pb-16 text-[#302621]">
      <BatchActionBar
        isSelectionMode={isSelectionMode}
        selectedCount={selectedCount}
        totalCount={filteredExpenses.length}
        onSelectAll={selectAll}
        onClearSelection={clearSelection}
        onBatchDelete={() => {
          if (confirm(`Yakin ingin menghapus ${selectedCount} biaya operasional terpilih?`)) {
            batchDeleteMutation.mutate(Array.from(selectedIds));
          }
        }}
        isDeleting={batchDeleteMutation.isPending}
      />
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 md:gap-4">
        <div>
          <h1 className="text-xl md:text-3xl font-black tracking-tight text-slate-800">Biaya Operasional</h1>
          <p className="text-[9px] md:text-xs text-slate-500 mt-0.5 md:mt-1">
            Catat dan pantau pengeluaran operasional berkala seperti bensin, listrik, air, internet, dan langganan software.
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={handleExportPDF}
            variant="outline" 
            className="gap-2 text-xs font-bold border-[#E6D5B8] text-[#8C6208] hover:bg-[#F9F5EC] rounded-xl h-10 shadow-xs"
          >
            <Download className="w-3.5 h-3.5" /> Ekspor PDF
          </Button>
          <Button 
            onClick={handleOpenAdd}
            className="bg-[#8C6208] text-white hover:bg-[#724F06] gap-2 text-xs font-bold rounded-xl h-10 shadow-sm"
          >
            <Plus className="w-4 h-4" /> Tambah Pengeluaran
          </Button>
        </div>
      </div>

      {/* Stats Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        {/* Total Expense Card */}
        <Card className="border-[#E6D5B8]/40 shadow-sm bg-white rounded-2xl overflow-hidden relative">
          <div className="absolute top-0 right-0 p-4 opacity-5">
            <DollarSign className="w-24 h-24" />
          </div>
          <CardContent className="p-4 md:p-6">
            <p className="text-[9px] md:text-[10px] text-slate-400 font-bold uppercase tracking-wider">Total Biaya Operasional</p>
            <p className="text-2xl md:text-3xl font-black text-slate-800 mt-1.5">Rp {fmt(totalAmount)}</p>
            <p className="text-[9px] md:text-[10px] text-slate-400 mt-1">Total akumulasi dari filter saat ini</p>
          </CardContent>
        </Card>

        {/* Largest Category */}
        <Card className="border-[#E6D5B8]/40 shadow-sm bg-white rounded-2xl overflow-hidden relative">
          <div className="absolute top-0 right-0 p-4 opacity-5">
            <ArrowUpDown className="w-24 h-24" />
          </div>
          <CardContent className="p-4 md:p-6">
            <p className="text-[9px] md:text-[10px] text-slate-400 font-bold uppercase tracking-wider">Kategori Terbesar</p>
            <p className="text-lg md:text-xl font-black text-slate-800 mt-2 truncate">{largestCategory.name}</p>
            <p className="text-[10px] md:text-xs text-rose-600 font-semibold mt-1">Rp {fmt(largestCategory.value)}</p>
          </CardContent>
        </Card>

        {/* Total Transactions */}
        <Card className="border-[#E6D5B8]/40 shadow-sm bg-white rounded-2xl overflow-hidden relative">
          <div className="absolute top-0 right-0 p-4 opacity-5">
            <FileText className="w-24 h-24" />
          </div>
          <CardContent className="p-4 md:p-6">
            <p className="text-[9px] md:text-[10px] text-slate-400 font-bold uppercase tracking-wider">Jumlah Transaksi</p>
            <p className="text-2xl md:text-3xl font-black text-slate-800 mt-1.5">{filteredExpenses.length} <span className="text-[10px] md:text-xs text-slate-400 font-bold uppercase">Record</span></p>
            <p className="text-[9px] md:text-[10px] text-slate-400 mt-1">Transaksi pengeluaran terdaftar</p>
          </CardContent>
        </Card>
      </div>

      {/* Category Progress Bars */}
      <Card className="border-[#E6D5B8]/40 shadow-sm bg-white rounded-2xl p-4 md:p-6">
        <h3 className="text-[10px] md:text-xs font-black uppercase text-slate-400 tracking-widest mb-4">Proporsi Biaya Operasional per Kategori</h3>
        <div className="space-y-4">
          {[...EXPENSE_CATEGORIES]
            .sort((a, b) => (categoryBreakdown[b.key] || 0) - (categoryBreakdown[a.key] || 0))
            .map(cat => {
            const val = categoryBreakdown[cat.key] || 0;
            const pct = totalAmount > 0 ? (val / totalAmount) * 100 : 0;
            const Icon = cat.icon;
            return (
              <div key={cat.key} className="space-y-1">
                <div className="flex justify-between items-center text-xs">
                  <div className="flex items-center gap-2">
                    <div className="p-1 rounded bg-slate-100 text-slate-600">
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <span className="font-bold text-slate-700">{cat.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-slate-800">Rp {fmt(val)}</span>
                    <span className="text-[10px] text-slate-400">({pct.toFixed(1)}%)</span>
                  </div>
                </div>
                <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                  <div 
                    className="bg-[#8C6208] h-full rounded-full transition-all duration-500" 
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Filter and CRUD List Table */}
      <Card className="border-[#E6D5B8]/40 shadow-sm bg-white rounded-2xl overflow-hidden">
        <div className="p-4 md:p-6 border-b border-slate-100 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 md:gap-4 bg-slate-50/50">
          <div>
            <h3 className="text-sm md:text-base font-bold text-slate-800">Daftar Transaksi Operasional</h3>
            <p className="text-[9px] md:text-xs text-slate-500">Kelola dan filter data pengeluaran operasional perusahaan.</p>
          </div>
        </div>

        {/* Filters */}
        <div className="p-4 bg-slate-50/20 border-b border-slate-100 flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-400 font-bold uppercase whitespace-nowrap">Kategori:</span>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="h-9 text-xs w-44 bg-white border-[#E6D5B8]/60">
                <SelectValue placeholder="Semua Kategori" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Semua Kategori</SelectItem>
                {EXPENSE_CATEGORIES.map(c => (
                  <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <Input 
              placeholder="Cari deskripsi atau kategori..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9 text-xs"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-400 font-bold uppercase whitespace-nowrap">Dari:</span>
            <CustomDatePicker 
              value={dateFrom} 
              onChange={setDateFrom} 
              className="h-9 text-xs w-36" 
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-400 font-bold uppercase whitespace-nowrap">S/d:</span>
            <CustomDatePicker 
              value={dateTo} 
              onChange={setDateTo} 
              className="h-9 text-xs w-36" 
            />
          </div>

          {(dateFrom || dateTo || search || categoryFilter !== 'all') && (
            <Button 
              variant="ghost" 
              onClick={() => { setDateFrom(''); setDateTo(''); setSearch(''); setCategoryFilter('all'); setCurrentPage(1); }}
              className="text-xs h-9 text-slate-500 hover:text-slate-800"
            >
              Reset
            </Button>
          )}
        </div>

        {/* Table list */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider border-b border-slate-200">
                {isSelectionMode && <th className="p-2 md:p-4 w-10 md:w-12"></th>}
                <th className="p-2 md:p-4 whitespace-nowrap text-[9px] md:text-xs">Tanggal</th>
                <th className="p-2 md:p-4 whitespace-nowrap text-[9px] md:text-xs">Kategori</th>
                <th className="p-2 md:p-4 text-[9px] md:text-xs">Deskripsi / Keterangan</th>
                <th className="p-2 md:p-4 text-right whitespace-nowrap text-[9px] md:text-xs">Jumlah</th>
                {!isSelectionMode && <th className="p-2 md:p-4 text-center whitespace-nowrap text-[9px] md:text-xs">Aksi</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
              {paginatedExpenses.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-slate-400">Belum ada pengeluaran operasional terdaftar.</td>
                </tr>
              ) : (
                paginatedExpenses.map((exp) => {
                  const meta = EXPENSE_CATEGORIES.find(c => c.key === exp.category) || {};
                  return (
                    <tr 
                      key={exp.id} 
                      onClick={() => isSelectionMode && toggleSelection(exp.id)}
                      className={`transition-colors ${isSelectionMode ? 'cursor-pointer' : ''} ${
                        selectedIds.has(exp.id) ? 'bg-rose-50/60' : 'hover:bg-slate-50/50'
                      }`}
                    >
                      {isSelectionMode && (
                        <td className="p-2 md:p-4">
                          <Checkbox checked={selectedIds.has(exp.id)} onCheckedChange={() => toggleSelection(exp.id)} />
                        </td>
                      )}
                      <td className="p-2 md:p-4 font-mono text-[9px] md:text-xs text-slate-400 whitespace-nowrap">{exp.date}</td>
                      <td className="p-2 md:p-4">
                        <div className="flex flex-col items-start gap-1">
                          <span className={`inline-block px-2 py-0.5 rounded text-[8px] md:text-[10px] font-bold border whitespace-nowrap ${meta.color || 'bg-slate-50 text-slate-700'}`}>
                            {exp.category}
                          </span>
                          <span className="text-[8px] md:text-[9px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">Peminta: {exp.requester || 'Office'}</span>
                        </div>
                      </td>
                      <td className="p-2 md:p-4 text-slate-700 text-[10px] md:text-xs font-semibold">{exp.description}</td>
                      <td className="p-2 md:p-4 text-right font-black text-xs md:text-sm text-slate-800 whitespace-nowrap">
                        Rp {fmt(exp.amount)}
                      </td>
                      {!isSelectionMode && (
                        <td className="p-2 md:p-4 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="w-7 h-7 hover:bg-slate-100 text-slate-500 hover:text-slate-800"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEdit(exp);
                              }}
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="w-7 h-7 hover:bg-rose-50 text-rose-500 hover:text-rose-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                startSelection(exp.id);
                              }}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination Controls ── */}
        {filteredExpenses.length > 0 && (
          <div className="px-6 py-4 border-t border-slate-100 bg-white flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="text-[11px] font-bold text-slate-500">Row per Page</span>
              <div className="w-[80px]">
                <Select value={String(itemsPerPage)} onValueChange={(val) => { setItemsPerPage(Number(val)); setCurrentPage(1); }}>
                  <SelectTrigger className="h-8 text-[11px] font-bold text-slate-700 bg-slate-50 border-slate-200/80 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="500">500</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="w-8 h-8 rounded-xl border-slate-200 text-slate-500"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                &lt;
              </Button>
              <div className="flex items-center justify-center w-8 h-8 rounded-xl bg-[#8C6208] text-white font-bold text-xs shadow-sm">
                {currentPage}
              </div>
              <Button
                variant="outline"
                size="icon"
                className="w-8 h-8 rounded-xl border-slate-200 text-slate-500"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                &gt;
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* ADD/EDIT FORM DIALOG MODAL */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black/40 -xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden border border-slate-100">
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 text-[#302621]">
              <h3 className="font-bold text-base text-slate-800">
                {editItem ? 'Edit Biaya Operasional' : 'Tambah Biaya Operasional'}
              </h3>
              <button onClick={() => setShowFormModal(false)} className="text-slate-400 hover:text-slate-600 text-lg">×</button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="p-6 space-y-4 text-xs text-[#302621]">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="font-bold uppercase tracking-wider text-[10px] text-slate-500">Tanggal</Label>
                    <CustomDatePicker 
                      value={form.date} 
                      onChange={val => setForm({ ...form, date: val })}
                      className="h-9"
                      required
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="font-bold uppercase tracking-wider text-[10px] text-slate-500">Kategori</Label>
                    <Select 
                      value={form.category} 
                      onValueChange={val => setForm({ ...form, category: val })}
                    >
                      <SelectTrigger className="h-9 bg-white">
                        <SelectValue placeholder="Pilih Kategori" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {EXPENSE_CATEGORIES.map(c => (
                          <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1 col-span-2 sm:col-span-1">
                    <Label className="font-bold uppercase tracking-wider text-[10px] text-slate-500">Divisi Peminta</Label>
                    <Select 
                      value={form.requester} 
                      onValueChange={val => setForm({ ...form, requester: val })}
                    >
                      <SelectTrigger className="h-9 bg-white">
                        <SelectValue placeholder="Pilih Divisi" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {REQUESTERS.map(r => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1 col-span-2 sm:col-span-1">
                    <Label className="font-bold uppercase tracking-wider text-[10px] text-slate-500">Sumber Rekening</Label>
                    <Select 
                      value={form.bankAccountId} 
                      onValueChange={val => setForm({ ...form, bankAccountId: val })}
                    >
                      <SelectTrigger className="h-9 bg-white border-blue-200 focus:ring-blue-500">
                        <div className="flex items-center gap-2">
                          <Landmark className="w-3.5 h-3.5 text-blue-500" />
                          <span>{accounts[form.bankAccountId]?.name || 'Pilih Rekening'}</span>
                        </div>
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="bca">{accounts.bca?.name || 'Apa Saja Bisa'}</SelectItem>
                        <SelectItem value="bni">{accounts.bni?.name || 'Satala Dermatech Essential'}</SelectItem>
                        <SelectItem value="mandiri">{accounts.mandiri?.name || 'Cuan Jalan Terus'}</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[9px] text-slate-400 mt-0.5">Saldo akan terpotong secara otomatis.</p>
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="font-bold uppercase tracking-wider text-[10px] text-slate-500">Nominal Pengeluaran (Rp)</Label>
                  <Input 
                    type="number"
                    placeholder="Contoh: 500000"
                    value={form.amount} 
                    onChange={e => setForm({ ...form, amount: e.target.value })} 
                    className="h-9"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <Label className="font-bold uppercase tracking-wider text-[10px] text-slate-500">Deskripsi / Keterangan</Label>
                  <Input 
                    placeholder="Contoh: Pembayaran Token Listrik Gedung B"
                    value={form.description} 
                    onChange={e => setForm({ ...form, description: e.target.value })} 
                    className="h-9"
                    required
                  />
                </div>

                <div className="space-y-2 border border-slate-100 rounded-xl p-3 bg-slate-50/50 mt-4">
                  <div className="flex justify-between items-center mb-1">
                    <Label className="font-bold text-[11px] uppercase tracking-wider text-slate-500">Alokasi Akun COA</Label>
                    <span className="text-[10px] font-medium text-slate-500">
                      Total COA: Rp {fmt((form.coa_allocations || []).reduce((s, c) => s + Number(c.amount || 0), 0))}
                    </span>
                  </div>
                  {(form.coa_allocations || []).map((coa, idx) => (
                    <div key={idx} className="flex gap-2 items-start relative group">
                      <div className="flex-1 grid grid-cols-12 gap-2">
                        <div className="col-span-3">
                          <Select 
                            value={coa.prefix} 
                            onValueChange={val => {
                              const newAlloc = [...form.coa_allocations];
                              newAlloc[idx].prefix = val;
                              newAlloc[idx].account_id = '';
                              newAlloc[idx].account_name = '';
                              setForm({ ...form, coa_allocations: newAlloc });
                            }}
                          >
                            <SelectTrigger className="h-8 text-[11px] bg-white border-slate-200">
                              <SelectValue placeholder="Kategori" />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                              <SelectItem value="1">1 - Aset</SelectItem>
                              <SelectItem value="2">2 - Kewajiban</SelectItem>
                              <SelectItem value="3">3 - Ekuitas</SelectItem>
                              <SelectItem value="4">4 - Pendapatan</SelectItem>
                              <SelectItem value="5">5 - HPP</SelectItem>
                              <SelectItem value="6">6 - Beban</SelectItem>
                              <SelectItem value="7">7 - Pdt Lain</SelectItem>
                              <SelectItem value="8">8 - Bbn Lain</SelectItem>
                              <SelectItem value="9">9 - Pajak</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-5">
                          <Select 
                            value={coa.account_id} 
                            onValueChange={val => {
                              const selectedCoa = coaList.find(c => c.id === val);
                              const newAlloc = [...form.coa_allocations];
                              newAlloc[idx].account_id = val;
                              newAlloc[idx].account_name = selectedCoa ? selectedCoa.name : '';
                              setForm({ ...form, coa_allocations: newAlloc });
                            }}
                          >
                            <SelectTrigger className="h-8 text-[11px] bg-white border-slate-200 text-left truncate">
                              <SelectValue placeholder="Pilih Akun" />
                            </SelectTrigger>
                            <SelectContent className="bg-white max-h-48">
                              {coaList.filter(c => !coa.prefix || String(c.id).startsWith(coa.prefix)).map(c => (
                                <SelectItem key={c.id} value={c.id} className="text-[11px]">
                                  <span className="font-mono text-slate-500 mr-1">{c.id}</span>{c.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-4">
                          <Input 
                            type="number"
                            placeholder="Nominal"
                            value={coa.amount}
                            onChange={e => {
                              const newAlloc = [...form.coa_allocations];
                              newAlloc[idx].amount = e.target.value;
                              setForm({ ...form, coa_allocations: newAlloc });
                            }}
                            className="h-8 text-[11px] border-slate-200"
                          />
                        </div>
                      </div>
                      <button 
                        type="button"
                        onClick={() => {
                          const newAlloc = form.coa_allocations.filter((_, i) => i !== idx);
                          setForm({ ...form, coa_allocations: newAlloc });
                        }}
                        className="w-8 h-8 flex items-center justify-center text-rose-500 hover:bg-rose-50 rounded-lg shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setForm({ ...form, coa_allocations: [...(form.coa_allocations || []), { prefix: '', account_id: '', amount: '' }] });
                    }}
                    className="w-full h-8 text-[11px] border-dashed text-slate-500 hover:text-[#8C6208] hover:border-[#8C6208] hover:bg-[#F9F5EC] gap-1 rounded-lg mt-2"
                  >
                    <Plus className="w-3 h-3" /> Tambah Akun COA
                  </Button>
                </div>
              </div>

              <div className="p-4 bg-slate-50 border-t border-slate-100 flex gap-2 justify-end text-xs">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowFormModal(false)}
                  className="rounded-xl h-9"
                >
                  Batal
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#8C6208] text-white hover:bg-[#724F06] rounded-xl h-9 font-bold"
                >
                  {editItem ? 'Simpan Perubahan' : 'Tambah Transaksi'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {asaConfirmData && (
        <AsaMutasiConfirm
          oldAmount={asaConfirmData.oldAmount}
          newAmount={asaConfirmData.newAmount}
          date={asaConfirmData.date}
          isProcessing={asaConfirmData.isProcessing}
          onConfirm={() => handleAsaConfirm(true)}
          onSkip={() => handleAsaConfirm(false)}
        />
      )}
    </div>
  );
}
